import './styles.css';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../Store/store';
import { playSound, SharedSoundEffect } from '../Sounds/SoundPlayer';

export enum ButtonStylizedType {
    Primary = 'button-primary',
    Secondary = 'button-secondary',
    Disabled = 'button-disabled',
    NoStyling = 'button-no-styling', // No styling, let caller override
}

export interface ButtonStylizedProps {
    buttonType: ButtonStylizedType;
    buttonHtmlType?: "button" | "submit" | "reset" | undefined; // An optional HTML button type, like 'submit' for forms
    text?: string;
    icon?: ReactNode;
    onClick?: () => void;
    children?: ReactNode;
    additionalStyles?: React.CSSProperties;
    additionalClasses?: string; // Optional additional CSS classes
    disableSound?: boolean; // Optional flag to disable the click sound
    disabled?: boolean; // Standard disabled prop
}

export const ButtonStylized: React.FC<ButtonStylizedProps> = ({
    buttonType,
    buttonHtmlType,
    text,
    icon,
    children,
    additionalStyles,
    additionalClasses,
    onClick,
    disableSound = false,
    disabled = false
}) => {
    const { currentGame } = useSelector((state: RootState) => state.gameSession);
    
    // Use game's primary color, fallback to default orange if no game is loaded
    const primaryColor = currentGame?.colorPrimary || 'rgb(247, 148, 29)';
    
    // Treat as disabled if either disabled prop is true or buttonType is Disabled
    const isDisabled = disabled || buttonType === ButtonStylizedType.Disabled;
    
    // Define dynamic styles based on button type and game colors
    const getDynamicStyles = (): React.CSSProperties => {
        const baseStyles: React.CSSProperties = {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: 'fit-content',
            padding: '1rem',
            fontFamily: 'Cubano, sans-serif',
            margin: 'auto',
            height: '4.3rem',
            textAlign: 'center',
            borderWidth: '0',
            borderRadius: '0.5rem',
            fontSize: '2rem',
            cursor: isDisabled ? 'default' : 'pointer'
        };

        switch (buttonType) {
            case ButtonStylizedType.Primary:
                return {
                    ...baseStyles,
                    backgroundColor: primaryColor,
                    color: 'white',
                    border: 'none'
                };
            
            case ButtonStylizedType.Secondary:
                return {
                    ...baseStyles,
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                    border: `3px solid ${primaryColor}`,
                    color: primaryColor
                };
            
            case ButtonStylizedType.Disabled:
                return {
                    ...baseStyles,
                    backgroundColor: 'rgba(200, 200, 200, 0.3)',
                    color: 'rgba(100, 100, 100, 0.8)',
                    border: '2px solid rgba(150, 150, 150, 0.4)',
                    cursor: 'default'
                };
            
            case ButtonStylizedType.NoStyling:
                return {
                    cursor: 'pointer'
                };
            
            default:
                return baseStyles;
        }
    };

    const dynamicStyles = getDynamicStyles();
    const finalStyles = { ...dynamicStyles, ...additionalStyles };

    // Handle hover effects
    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) return;
        
        const button = e.currentTarget;
        if (buttonType === ButtonStylizedType.Primary) {
            button.style.opacity = '0.9';
        } else if (buttonType === ButtonStylizedType.Secondary) {
            button.style.backgroundColor = `${primaryColor}0D`; // 5% opacity
        }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) return;
        
        const button = e.currentTarget;
        if (buttonType === ButtonStylizedType.Primary) {
            button.style.opacity = '1';
        } else if (buttonType === ButtonStylizedType.Secondary) {
            button.style.backgroundColor = 'rgba(255, 255, 255, 0)';
        }
    };

    const className = buttonType === ButtonStylizedType.NoStyling ? '' : 'mental-mint-button-base';
    const fullClassName = additionalClasses ? `${className} ${additionalClasses}` : className;

    const handleClick = () => {
        if (!isDisabled) {
            if (!disableSound) {
                playSound(SharedSoundEffect.MENU_CLICK);
            }
            onClick?.();
        }
    };

    return (
        <button 
            className={fullClassName}
            type={buttonHtmlType || "button"} 
            disabled={isDisabled} 
            onClick={handleClick}
            style={finalStyles}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {icon && <span className="button-icon">{icon}</span>}
            {text && <span className="button-text">{text}</span>}
            {children && <span className="button-children">{children}</span>}
        </button>
    );
};